/**
 * Rotation Planner — la pizarra de rotaciones del DT, en datos.
 *
 * Dado un sexteto inicial (camiseta por posición P1..P6) el planner produce
 * las 6 rotaciones con quién queda en cada zona, los SOLAPAMIENTOS que la
 * regla obliga a respetar en cada una, y el cruce con la estadística por
 * rotación (`buildRotationReport`) cuando hay datos del partido o de la
 * temporada.
 *
 * ## Convenciones (las mismas del resto del repo)
 *
 *  - Una formación es `number[]` con índice 0 = P1 (zaguero derecho, el que
 *    saca); rotar es `P2→P1, P3→P2 … P1→P6` (`rotateForward`, idéntico al
 *    motor y a `projections.ts`).
 *  - "Rotación N" se identifica por la POSICIÓN DEL ARMADOR (convención
 *    DataVolley, la misma de `RotationLine.rotation`): así el cruce con la
 *    estadística es un join por `setterPosition` y no una adivinanza.
 *
 * ## Solapamientos (regla 7.4 FIVB)
 *
 * En el momento del saque cada jugador tiene que respetar su posición
 * RELATIVA: el zaguero detrás de su delantero correspondiente (P1 detrás de
 * P2, P6 detrás de P3, P5 detrás de P4) y, dentro de cada línea, el orden
 * izquierda→derecha (P4–P3–P2 adelante, P5–P6–P1 atrás). Son 7 restricciones
 * por rotación, siempre las mismas ZONAS pero cada vez otros JUGADORES: eso
 * es exactamente lo que el planner enumera con nombre y apellido, porque es
 * lo que se ensaya en el entrenamiento.
 *
 * Todo puro y determinístico: entra una formación, salen datos. Sin DOM, sin
 * red, sin dependencias nuevas.
 */
import type { TeamSide } from '@scout/core';
import { ratio, type NormalizedEvent, type TeamMeta } from './log';
import type { RotationLine, TeamRotationReport } from './projections';

/** Posiciones de vóley indoor. */
export const PLANNER_POSITIONS = 6;

/**
 * La cancha como se dibuja (desde atrás del equipo propio, red arriba):
 * fila delantera Z4–Z3–Z2, fila zaguera Z5–Z6–Z1.
 */
export const COURT_ROWS: ReadonlyArray<readonly number[]> = [
  [4, 3, 2],
  [5, 6, 1],
];

/** Un lugar del sexteto: la posición y la camiseta que la ocupa (o nada). */
export type Lineup = ReadonlyArray<number | null>;

export interface OverlapPair {
  /** `frontBack`: `a` es el delantero y `b` su zaguero. `leftRight`: `a` queda a la izquierda de `b`. */
  kind: 'frontBack' | 'leftRight';
  zoneA: number;
  zoneB: number;
  playerA: number | null;
  playerB: number | null;
}

/** Una de las 6 rotaciones del plan. */
export interface PlannedRotation {
  /** 0 = la formación inicial tal cual; 1..5 = tras N rotaciones. */
  index: number;
  /** Camiseta por posición, índice 0 = P1. */
  formation: Lineup;
  /** Posición del armador (1..6) en ESTA formación; null = sin armador elegido. */
  setterPosition: number | null;
  /** Las 7 restricciones de la regla 7.4, con los jugadores de esta rotación. */
  overlaps: OverlapPair[];
}

/** Delantero → su zaguero (P2/P1, P3/P6, P4/P5). */
const FRONT_BACK: ReadonlyArray<readonly [number, number]> = [
  [2, 1],
  [3, 6],
  [4, 5],
];

/** Izquierda → derecha por línea (P4–P3–P2 y P5–P6–P1). */
const LEFT_RIGHT: ReadonlyArray<readonly [number, number]> = [
  [4, 3],
  [3, 2],
  [5, 6],
  [6, 1],
];

function playerAt(formation: Lineup, zone: number): number | null {
  return formation[zone - 1] ?? null;
}

/** Las restricciones de solapamiento de UNA formación. */
export function overlapsOf(formation: Lineup): OverlapPair[] {
  const out: OverlapPair[] = [];
  for (const [front, back] of FRONT_BACK) {
    out.push({
      kind: 'frontBack',
      zoneA: front,
      zoneB: back,
      playerA: playerAt(formation, front),
      playerB: playerAt(formation, back),
    });
  }
  for (const [left, right] of LEFT_RIGHT) {
    out.push({
      kind: 'leftRight',
      zoneA: left,
      zoneB: right,
      playerA: playerAt(formation, left),
      playerB: playerAt(formation, right),
    });
  }
  return out;
}

/** Rota una formación: P2→P1, P3→P2 … P1→P6 (misma regla que el motor), tolerando huecos. */
export function rotateLineup(lineup: Lineup): Lineup {
  if (lineup.length <= 1) return [...lineup];
  const first = lineup[0] ?? null;
  return [...lineup.slice(1), first];
}

/**
 * Las 6 rotaciones a partir del sexteto inicial, en el ORDEN en que se juegan
 * (la inicial primero; cada sideout propio rota una vez).
 */
export function planRotations(lineup: Lineup, setter: number | null): PlannedRotation[] {
  const out: PlannedRotation[] = [];
  let formation: Lineup = [...lineup];
  for (let index = 0; index < PLANNER_POSITIONS; index++) {
    const setterIdx = setter === null ? -1 : formation.indexOf(setter);
    out.push({
      index,
      formation,
      setterPosition: setterIdx >= 0 ? setterIdx + 1 : null,
      overlaps: overlapsOf(formation),
    });
    formation = rotateLineup(formation);
  }
  return out;
}

/** Camisetas repetidas en el sexteto (para avisar antes de dibujar nada). */
export function duplicatedNumbers(lineup: Lineup): number[] {
  const seen = new Set<number>();
  const dup = new Set<number>();
  for (const n of lineup) {
    if (n === null) continue;
    if (seen.has(n)) dup.add(n);
    seen.add(n);
  }
  return [...dup].sort((a, b) => a - b);
}

/** ¿Está el sexteto completo (6 camisetas, sin repetidas)? */
export function lineupComplete(lineup: Lineup): boolean {
  return (
    lineup.length === PLANNER_POSITIONS &&
    lineup.every((n) => n !== null) &&
    duplicatedNumbers(lineup).length === 0
  );
}

// ---------------------------------------------------------------------------
// Sexteto inicial desde el log (para precargar la pizarra con un partido real)
// ---------------------------------------------------------------------------

export interface LineupFromLog {
  lineup: number[];
  /** Camiseta del armador: `setterPosition` del lineup manda; si no, el rol del plantel. */
  setter: number | null;
}

/**
 * El primer `lineup_set` de un equipo en el log (misma prioridad que
 * `buildRotationReport`: `setterPosition` explícito y, si falta, el rol
 * declarado en el plantel).
 */
export function firstLineup(
  all: readonly NormalizedEvent[],
  side: TeamSide,
  team: TeamMeta,
): LineupFromLog | null {
  for (const { event } of all) {
    if (event.type !== 'lineup_set' || event.payload.team !== side) continue;
    const lineup = [...event.payload.rotation];
    const byPosition =
      event.payload.setterPosition !== undefined
        ? lineup[event.payload.setterPosition - 1]
        : undefined;
    const setter =
      byPosition ?? lineup.find((n) => team.roles.get(n) === 'setter') ?? null;
    return { lineup, setter };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Cruce con la estadística por rotación (partido o temporada)
// ---------------------------------------------------------------------------

/** La línea P{setterPosition} del reporte, si el equipo está trackeado. */
export function statsForRotation(
  team: TeamRotationReport | null | undefined,
  setterPosition: number | null,
): RotationLine | null {
  if (!team || !team.tracked || setterPosition === null) return null;
  return team.rotations.find((line) => line.rotation === setterPosition) ?? null;
}

/**
 * Acumula los reportes de rotación de VARIOS partidos (la temporada del
 * equipo): se suman los CONTEOS por rotación y recién después se divide, con
 * la misma regla de siempre (promediar porcentajes daría cualquier cosa).
 * Los equipos sin rotación reconstruible no aportan (ni ruido ni ceros).
 */
export function sumRotationReports(
  teams: ReadonlyArray<TeamRotationReport>,
): TeamRotationReport | null {
  const tracked = teams.filter((team) => team.tracked);
  if (tracked.length === 0) return null;

  const acc = Array.from({ length: PLANNER_POSITIONS }, (_, i) => ({
    rotation: i + 1,
    label: `P${i + 1}`,
    rallies: 0,
    ralliesServed: 0,
    ralliesReceived: 0,
    pointsWon: 0,
    pointsLost: 0,
    breakPoints: 0,
    sideoutPoints: 0,
  }));
  let unassigned = 0;

  for (const team of tracked) {
    unassigned += team.unassignedRallies;
    for (const line of team.rotations) {
      const cell = acc[line.rotation - 1];
      if (!cell) continue;
      cell.rallies += line.rallies;
      cell.ralliesServed += line.ralliesServed;
      cell.ralliesReceived += line.ralliesReceived;
      cell.pointsWon += line.pointsWon;
      cell.pointsLost += line.pointsLost;
      cell.breakPoints += line.breakPoints;
      cell.sideoutPoints += line.sideoutPoints;
    }
  }

  return {
    name: tracked[0]?.name ?? '',
    tracked: true,
    rotations: acc.map((cell) => ({
      ...cell,
      netPoints: cell.pointsWon - cell.pointsLost,
      sideoutPct: ratio(cell.sideoutPoints, cell.ralliesReceived),
      breakPct: ratio(cell.breakPoints, cell.ralliesServed),
    })),
    unassignedRallies: unassigned,
  };
}
